import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export const PICTURE_BASE_URL = 'https://stpublicclient.blob.core.windows.net/teamhaven/Pictures';

// ---- Pictures -------------------------------------------------------------

export const getPictureCallId = (picture) => picture.CALLID || picture.callid;

export const getPictureUrl = (picture) =>
  picture.PICTUREURL || picture.pictureurl || picture.picture_url || picture.path;

export const getPictureCaption = (picture) =>
  picture.TRANSLATEDQUESTION || picture.translatedquestion || picture.QUESTION || picture.question || '';

export const isVisiblePicture = (picture) =>
  !(picture.ADMIN_ONLY || picture.admin_only || picture.DELETEDAT || picture.deletedat);

export const getPictureSrc = (picture) => {
  const url = getPictureUrl(picture);
  if (!url) return null;
  return url.startsWith('https') ? url : `${PICTURE_BASE_URL}/${url}`;
};

// ---- Dates ----------------------------------------------------------------

// Duration (in minutes) between two consecutive visits, human-readable
export const formatTimeDiff = (minutes) => {
  if (!minutes && minutes !== 0) return '';
  if (minutes < 90) return `${Math.round(minutes)} min`;
  if (minutes < 72 * 60) return `${Math.round(minutes / 60)} h`;
  return `${Math.round(minutes / (60 * 24))} j`;
};

export const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDateFr = (value, opts = { day: '2-digit', month: 'long', year: 'numeric' }) => {
  const d = parseDate(value);
  return d ? d.toLocaleDateString('fr-FR', opts) : '';
};

export const formatTimeFr = (value) => {
  const d = parseDate(value);
  return d ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
};

// "il y a 3 jours"
export const formatRelativeDate = (value) => {
  const d = parseDate(value);
  if (!d) return '';
  return formatDistanceToNow(d, { addSuffix: true, locale: fr });
};

// ---- GPS status / problems ------------------------------------------------

// A GPS ticket is "open" when its status_category is not yet DONE
export const isOpenCall = (call) =>
  call && call.type === 'GPS' && call.statusCategory && call.statusCategory !== 'DONE';

// Parse the GPS "problems" field (JSON array, comma-separated string, or array) and dedupe
export const parseProblems = (problems) => {
  if (!problems) return [];
  let list = problems;
  if (typeof problems === 'string') {
    try {
      const parsed = JSON.parse(problems);
      list = Array.isArray(parsed) ? parsed : String(problems).split(',');
    } catch (e) {
      list = problems.split(',');
    }
  }
  if (!Array.isArray(list)) return [];
  return [...new Set(list.map((p) => String(p).trim()).filter(Boolean))];
};

// Color a GPS status badge based on its (French) label
export const getStatusBadgeClasses = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('termin') || s.includes('approuv')) return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
  if (s.includes('annul') || s.includes('rejet') || s.includes('declin')) return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  if (s.includes('standby') || s.includes('attente')) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  if (s.includes('cours') || s.includes('qualif') || s.includes('programm')) return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300';
  return 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
};

// Split a comma-separated furniture string into trimmed tokens
const splitFurniture = (value) =>
  String(value || '')
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean);

// ---- Mobiliers derivation -------------------------------------------------

// Build the store's furniture list from assets metadata, enriched with the
// related GPS tickets (brand, last visit, ticket count, latest conformity).
export const deriveMobiliers = (calls = []) => {
  const assetsCalls = calls.filter((c) => c.type === 'assets' && c.furnitures);
  const gpsCalls = calls.filter((c) => c.type === 'GPS');

  const names = new Set();
  assetsCalls.forEach((c) => splitFurniture(c.furnitures).forEach((n) => names.add(n)));
  // Fallback: if no assets metadata, derive names from GPS tickets
  if (names.size === 0) {
    gpsCalls.forEach((c) => splitFurniture(c.furnitures).forEach((n) => names.add(n)));
  }

  return [...names]
    .map((name) => {
      const related = gpsCalls.filter((c) => splitFurniture(c.furnitures).includes(name));
      const dated = related.filter((c) => c.date).sort((a, b) => new Date(b.date) - new Date(a.date));
      const latest = dated[0] || related[0] || null;

      // Conformity is driven by the LATEST ticket:
      //  - no ticket            -> 'none'
      //  - latest still open     -> 'pending' (intervention en cours)
      //  - latest done + KO      -> 'nonconform'
      //  - latest done (else)    -> 'conform'
      let conformity = 'none';
      if (latest) {
        if (isOpenCall(latest)) conformity = 'pending';
        else if (latest.interventionCompliant === false) conformity = 'nonconform';
        else conformity = 'conform';
      }

      return {
        name,
        brand: latest?.brand || null,
        lastVisit: dated[0]?.date || null,
        ticketCount: related.length,
        openCount: related.filter(isOpenCall).length,
        conformity,
      };
    })
    .sort((a, b) => b.ticketCount - a.ticketCount || a.name.localeCompare(b.name));
};

// ---- Misc -----------------------------------------------------------------

// Display label for a project key (GPS internal key shown as "GPS")
export const projectLabel = (proj) => (proj === 'LS' ? 'GPS' : proj);

export const getInitial = (name) => (name || '?').trim().charAt(0).toUpperCase();
